/**
 * ReportHeatmap.js — Time-of-day anomaly heatmap
 */
import React from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid } from "recharts";
import { SEVERITY_COLORS } from "../reportUtils";

const HOUR_LABELS = Array.from({length: 24}, (_, i) =>
  `${String(i).padStart(2,"0")}:00`
);

const getShiftLabels = (theme) => ({
  night:    { hours: [0,1,2,3,4,5],        label: "Night Shift",     color: theme.colors.accent },
  morning:  { hours: [6,7,8,9,10,11],      label: "Morning Shift",   color: "#2E7D32" },
  afternoon:{ hours: [12,13,14,15,16,17],  label: "Afternoon Shift", color: "#F57F17" },
  evening:  { hours: [18,19,20,21,22,23],  label: "Evening Shift",   color: theme.colors.accent },
});

export default function ReportHeatmap({ stats, theme, SEVERITY_COLORS: SC }) {
  if (!stats) return null;

  const hourData = stats.hourDist.map((count, i) => ({
    hour:    HOUR_LABELS[i],
    events:  count,
    pct:     stats.total > 0 ? (count / stats.total) * 100 : 0,
  }));

  const maxCount = Math.max(...stats.hourDist);
  const peakHour = stats.hourDist.indexOf(maxCount);

  const shiftTotals = Object.entries(getShiftLabels(theme)).map(([key, { hours, label, color }]) => ({
    label, color,
    total: hours.reduce((sum, h) => sum + stats.hourDist[h], 0),
    hours,
  }));

  const hasTimestamp = stats?.hasValidTime && stats?.timeCol;
  const hasData = hasTimestamp && stats.hourDist.some(v => v > 0);

  // Intensity color based on count
  const getIntensityColor = (count) => {
    if (!maxCount) return "#E3F2FD";
    const ratio = count / maxCount;
    if (ratio >= 0.8) return SC.critical;
    if (ratio >= 0.6) return SC.high;
    if (ratio >= 0.4) return SC.medium;
    if (ratio >= 0.2) return SC.low;
    return "#E3F2FD";
  };

  return (
    <div className="report-section page-break" style={{ marginBottom: 24 }}>
      <SectionTitle color="#F57F17">Anomaly Heatmap — Time of Day</SectionTitle>

      {!hasTimestamp ? (
        <div style={{ padding: 20, background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 10, fontFamily: "monospace", fontSize: 12, color: theme.colors.textMuted, textAlign: "center" }}>
          No real timestamp column in this dataset — flow feature datasets (CIC-IDS2017, UNSW-NB15) do not contain wall-clock timestamps
          <div style={{ marginTop: 8, fontSize: 11, color: theme.colors.textMuted }}>Heatmap populates when dataset contains a Timestamp or datetime column</div>
        </div>
      ) : !hasData ? (
        <div style={{ padding: 20, background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 10, fontFamily: "monospace", fontSize: 12, color: theme.colors.textMuted, textAlign: "center" }}>
          Timestamp column detected but no valid dates could be parsed
        </div>
      ) : (
        <>
          {/* Heatmap grid */}
          <div className="report-card" style={{ background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 10, padding: 20, marginBottom: 16 }}>
            <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textMuted, letterSpacing: 1, marginBottom: 16 }}>
              EVENT INTENSITY BY HOUR — Peak: {HOUR_LABELS[peakHour]} ({maxCount.toLocaleString()} events)
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(24, 1fr)", gap: 3 }}>
              {hourData.map((d, i) => (
                <div key={i} title={`${d.hour}: ${d.events.toLocaleString()} events`}>
                  <div style={{
                    height: 48, background: getIntensityColor(d.events),
                    borderRadius: 4, display: "flex", alignItems: "center",
                    justifyContent: "center", cursor: "default",
                    border: i === peakHour ? `2px solid ${theme.colors.textPrimary}` : `1px solid ${theme.colors.cardBorder}`
                  }}>
                    <span style={{ fontSize: 8, fontFamily: "monospace", color: d.events > maxCount * 0.5 ? theme.colors.brandText : theme.colors.textPrimary, fontWeight: 700 }}>
                      {d.events > 0 ? d.events : ""}
                    </span>
                  </div>
                  <div style={{ textAlign: "center", fontSize: 7, fontFamily: "monospace", color: theme.colors.textMuted, marginTop: 2 }}>
                    {i % 6 === 0 ? d.hour : ""}
                  </div>
                </div>
              ))}
            </div>

            {/* Legend */}
            <div style={{ display: "flex", gap: 12, marginTop: 16, alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ fontFamily: "monospace", fontSize: 10, color: theme.colors.textMuted }}>INTENSITY:</span>
              {[
                { label: "Very High (≥80%)", color: SC.critical },
                { label: "High (≥60%)",      color: SC.high     },
                { label: "Medium (≥40%)",    color: SC.medium   },
                { label: "Low (≥20%)",       color: SC.low      },
                { label: "Minimal (<20%)",   color: "#E3F2FD"   },
              ].map(({ label, color }) => (
                <div key={label} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <div style={{ width: 12, height: 12, background: color, borderRadius: 2, border: `1px solid ${theme.colors.cardBorder}` }} />
                  <span style={{ fontFamily: "monospace", fontSize: 9, color: theme.colors.textMuted }}>{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Bar chart */}
          <div className="report-card chart-container" style={{ background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 10, padding: 20, marginBottom: 16 }}>
            <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textMuted, letterSpacing: 1, marginBottom: 12 }}>HOURLY EVENT VOLUME</div>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={hourData} margin={{ top: 5, right: 20, bottom: 20, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.cardBorder} />
                <XAxis dataKey="hour" tick={{ fontSize: 8, fill: theme.colors.textMuted }} interval={5} />
                <YAxis tick={{ fontSize: 10, fill: theme.colors.textMuted }} />
                <Tooltip contentStyle={{ background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`, fontSize: 12 }} formatter={(v) => [v.toLocaleString(), "Events"]} />
                <Bar dataKey="events" radius={[2,2,0,0]}>
                  {hourData.map((d, i) => <Cell key={i} fill={getIntensityColor(d.events)} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Shift analysis */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
            {shiftTotals.map(({ label, color, total }) => (
              <div key={label} style={{ background: `${color}12`, border: `1px solid ${color}33`, borderRadius: 8, padding: "12px 16px", textAlign: "center" }}>
                <div style={{ fontFamily: "monospace", fontSize: 20, fontWeight: 900, color }}>{total.toLocaleString()}</div>
                <div style={{ fontFamily: "monospace", fontSize: 10, color: theme.colors.textMuted, letterSpacing: 1, marginTop: 2 }}>{label.toUpperCase()}</div>
                <div style={{ fontFamily: "monospace", fontSize: 9, color: theme.colors.textMuted }}>
                  {stats.total > 0 ? ((total/stats.total)*100).toFixed(1) : 0}% of total
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const SectionTitle = ({ children, color }) => (
  <div style={{ fontFamily: "monospace", fontSize: 13, fontWeight: 900, color, letterSpacing: 2, textTransform: "uppercase", borderLeft: `4px solid ${color}`, paddingLeft: 12, marginBottom: 16 }}>{children}</div>
);
